import css from './ListingpageContent.module.css';
import { AvatarLarge } from '../../components';
import Icons from '../../components/Icons/Icons';
import { FormattedMessage } from 'react-intl';
import { useConfiguration } from '../../context/configurationContext';
import { formatMoney } from '../../util/currency';
import { capitalizeFirst, getEnumLabels } from '../../util/genericHelpers';

const getNumber = (...values) => {
  const found = values.find(value => value !== null && value !== undefined && value !== '');
  const number = Number(found);
  return Number.isFinite(number) ? number : null;
};

const getServicePrice = service => {
  const price = Number(service?.price);
  return Number.isFinite(price) && price > 0 ? price : null;
};

const formatPlainMoney = (intl, amount, currency = 'USD') =>
  intl.formatNumber(amount, { style: 'currency', currency });

const toTitleCase = value =>
  `${value}`
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const formatSpecialtyTagline = value => {
  const tagline = `${value || ''}`.trim();
  if (!tagline) {
    return 'Professional chef services';
  }

  const cuisineMatch = tagline.match(/^(.+?)\s+cuisine$/i);
  return cuisineMatch ? `${toTitleCase(cuisineMatch[1])} Cuisine Specialist` : tagline;
};

function ListingHeroContent(props) {
  const { intl, author, title, listing, reviews = [] } = props;

  // Extract dynamic data from listing publicData
  const { publicData, geolocation, metadata = {}, price } = listing?.attributes || {};
  const tagline = formatSpecialtyTagline(publicData?.tagline);
  const dietaryOptions = publicData?.dietaryOptions || [];
  const allergenHandling = publicData?.allergenHandlingText || [];
  const languages = publicData?.languages || ['English'];
  const services = Array.isArray(publicData?.services) ? publicData.services : [];
  const config = useConfiguration();
  const formattedDietaryOptions = getEnumLabels(
    config?.listing?.listingFields,
    'dietaryOptions',
    dietaryOptions
  );
  const formattedLanguageOptions = getEnumLabels(
    config?.listing?.listingFields,
    'languages',
    languages
  );
  const combinedPills = [
    ...formattedDietaryOptions,
    ...(Array.isArray(allergenHandling) ? allergenHandling : []),
  ].map(capitalizeFirst);

  const reviewRatings = reviews
    .map(review => getNumber(review?.attributes?.rating))
    .filter(rating => rating !== null);
  const ratingFromReviews = reviewRatings.length
    ? reviewRatings.reduce((sum, rating) => sum + rating, 0) / reviewRatings.length
    : null;
  const rating = getNumber(
    publicData?.rating,
    publicData?.averageRating,
    publicData?.customerRating,
    metadata?.rating,
    metadata?.averageRating,
    metadata?.customerRating,
    ratingFromReviews
  );
  const reviewCount =
    getNumber(
      publicData?.reviewCount,
      publicData?.reviewsCount,
      publicData?.numberOfReviews,
      metadata?.reviewCount,
      metadata?.reviewsCount,
      metadata?.numberOfReviews
    ) ?? reviews.length;
  const hasRating = rating !== null && reviewCount > 0;
  const formattedRating = hasRating ? rating.toFixed(1).replace(/\.0$/, '') : null;

  const servicePrices = services.map(getServicePrice).filter(servicePrice => servicePrice !== null);
  const lowestServicePrice = servicePrices.length ? Math.min(...servicePrices) : null;
  const priceText = lowestServicePrice
    ? formatPlainMoney(intl, lowestServicePrice, publicData?.currency || config.currency)
    : price
    ? formatMoney(intl, price)
    : null;

  // Extract address and service radius from publicData
  const address = publicData?.location?.address || geolocation?.address || 'Location not set';
  const serviceRadius = publicData?.serviceRadius || '8';
  const addressText = `Based in ${address} (Travel up to ${serviceRadius} km)`;

  // Extract languages (you might need to add this to your listing form)
  const languageText = `Speaks ${formattedLanguageOptions.join(', ')}`;
  return (
    <div className={css.usersInfoSec}>
      <AvatarLarge user={author} className={css.userAvatar} />
      <div className={css.userInfo}>
        <h2>
          <FormattedMessage id="ListingPage.orderTitle" values={{ title: title }} />
        </h2>
        <p>{tagline}</p>
        {priceText ? (
          <div className={css.priceSummary}>
            <FormattedMessage id="ListingHeroContent.priceSummary" values={{ price: priceText }} />
          </div>
        ) : null}
        <div className={css.ratingSec}>
          {hasRating ? (
            <>
              <span className={css.rating}>
                <Icons name="star" /> {formattedRating}
              </span>
              <span className={css.review}>
                <FormattedMessage
                  id="ListingHeroContent.reviewCount"
                  values={{ count: reviewCount }}
                />
              </span>
            </>
          ) : null}
          {combinedPills.length > 0 && (
            <>
              {hasRating ? ' • ' : null}
              {combinedPills.map((pill, index) => (
                <span key={index} className={css.otherPills}>
                  {pill}
                </span>
              ))}
            </>
          )}
        </div>
        <div className={css.locationSec}>
          <span className={css.location}>
            <Icons name="location" /> {addressText}
          </span>
          <span className={css.language}>
            <Icons name="web" /> {languageText}
          </span>
          {/* {combinedPills.map((pill, index) => (
            <span key={`pill-${index}`} className={css.pill}>{pill}</span>
          ))} */}
        </div>
      </div>
    </div>
  );
}

export default ListingHeroContent;
